import React, { useEffect, useState } from 'react';
import { Card, Segmented, Tag, Space, Button, Alert, Empty, Popconfirm, Typography } from 'antd';
import { EditOutlined, EyeOutlined, SendOutlined, UndoOutlined, DeleteOutlined } from '@ant-design/icons';
import NoteBlocksEditor from './NoteBlockEditor';
import NoteBlockRenderer, { NoteBlock } from './NoteBlockRenderer';
import { PocketCardsEditor, TimelineEditor, FlashCard, TimelineEvent } from './PocketAndTimelineEditors';
import type { NcertChapterNote } from './types';

const { Text, Title } = Typography;

interface Props {
  chapterTitle: string;
  notes: NcertChapterNote[];
  subTab: 'important' | 'pocket' | 'timeline';
  onSubTabChange: (t: 'important' | 'pocket' | 'timeline') => void;
  mode: 'edit' | 'preview';
  onModeChange: (m: 'edit' | 'preview') => void;
  saving: boolean;
  onSaveContent: (content: object) => Promise<void>;
  onSetStatus: (id: string, status: 'pending_review' | 'published') => Promise<void>;
  onDelete: (id: string) => Promise<void>;
}

const NotesTabPanel: React.FC<Props> = ({
  chapterTitle, notes, subTab, onSubTabChange, mode, onModeChange,
  saving, onSaveContent, onSetStatus, onDelete,
}) => {
  const current = notes.find((n) => n.note_type === subTab) || null;

  const [blocks, setBlocks] = useState<NoteBlock[]>([]);
  const [cards, setCards] = useState<FlashCard[]>([]);
  const [events, setEvents] = useState<TimelineEvent[]>([]);

  // Re-sync the local draft whenever the selected note (chapter or sub-tab) changes —
  // never while the admin is mid-edit on the same note.
  useEffect(() => {
    setBlocks(current?.content.blocks ?? []);
    setCards(current?.content.cards ?? []);
    setEvents(current?.content.events ?? []);
  }, [current?.id, subTab]);

  const handleSave = () => {
    if (subTab === 'important') return onSaveContent({ blocks });
    if (subTab === 'pocket') return onSaveContent({ cards });
    return onSaveContent({ events });
  };

  return (
    <Card title={`Notes — ${chapterTitle}`}>
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <Space style={{ width: '100%', justifyContent: 'space-between' }} wrap>
          <Segmented
            value={subTab}
            onChange={(v) => onSubTabChange(v as any)}
            options={[
              { label: 'Important Notes', value: 'important' },
              { label: 'Pocket Notes', value: 'pocket' },
              { label: 'Timeline', value: 'timeline' },
            ]}
          />
          <Space>
            {current && (
              <Tag color={current.status === 'published' ? 'success' : 'warning'}>
                {current.status === 'published' ? 'Published' : 'Pending Review'}
              </Tag>
            )}
            <Segmented
              value={mode}
              onChange={(v) => onModeChange(v as any)}
              options={[
                { label: <><EditOutlined /> Edit</>, value: 'edit' },
                { label: <><EyeOutlined /> Preview</>, value: 'preview' },
              ]}
            />
          </Space>
        </Space>

        {current?.grounding_note && (
          <Alert
            type="warning"
            showIcon
            message="Flagged for review"
            description={`The generator flagged possible issues in this content — check these before publishing: ${current.grounding_note}`}
          />
        )}

        {mode === 'edit' ? (
          <>
            {subTab === 'important' && <NoteBlocksEditor blocks={blocks} onChange={setBlocks} />}
            {subTab === 'pocket' && <PocketCardsEditor cards={cards} onChange={setCards} />}
            {subTab === 'timeline' && <TimelineEditor events={events} onChange={setEvents} />}

            <Space>
              <Button type="primary" loading={saving} onClick={handleSave}>Save</Button>
              {current && current.status === 'pending_review' && (
                <Button icon={<SendOutlined />} onClick={() => onSetStatus(current.id, 'published')}>
                  Publish
                </Button>
              )}
              {current && current.status === 'published' && (
                <Button icon={<UndoOutlined />} onClick={() => onSetStatus(current.id, 'pending_review')}>
                  Unpublish
                </Button>
              )}
              {current && (
                <Popconfirm title="Delete this note entirely?" onConfirm={() => onDelete(current.id)}>
                  <Button danger icon={<DeleteOutlined />}>Delete</Button>
                </Popconfirm>
              )}
            </Space>
          </>
        ) : (
          <Card size="small" style={{ background: '#fafafa' }}>
            {subTab === 'important' && (
              blocks.length > 0
                ? blocks.map((b, i) => <NoteBlockRenderer key={i} block={b} />)
                : <Empty description="No content yet" />
            )}
            {subTab === 'pocket' && (
              cards.length > 0 ? (
                <Space direction="vertical" style={{ width: '100%' }}>
                  {cards.map((c, i) => (
                    <Card key={i} size="small" style={{ borderLeft: '3px solid #1D4ED8' }}>
                      <Text strong>Q: </Text><Text>{c.front}</Text><br />
                      <Text strong>A: </Text><Text>{c.back}</Text>
                    </Card>
                  ))}
                </Space>
              ) : <Empty description="No flashcards yet" />
            )}
            {subTab === 'timeline' && (
              events.length > 0 ? (
                <Space direction="vertical" style={{ width: '100%' }}>
                  {events.map((e, i) => (
                    <Card key={i} size="small" style={{ borderLeft: '3px solid #059669' }}>
                      <Text strong>{e.period}</Text> — <Text strong>{e.title}</Text>
                      <div><Text type="secondary">{e.description}</Text></div>
                    </Card>
                  ))}
                </Space>
              ) : <Empty description="No timeline for this chapter" />
            )}
          </Card>
        )}
      </Space>
    </Card>
  );
};

export default NotesTabPanel;
